/**
 * Product Projects — portfolio management.
 *
 * Faithfully reflects the company SOP structure: identity / ownership /
 * purpose / pre-planning / milestones / post-effects / evaluation /
 * readiness gate / portfolio control / run-and-update / meta.
 *
 * Collections:
 *   /productProjects/{id}        — the project document
 *   /projectActionItems/{id}     — cross-project action items (top-level so
 *                                  assignee + overdue queries are cheap)
 *
 * Color/flag semantics for the web UI are exported as helpers at the bottom.
 */
// ===========================================================================
// REQUEST LIFECYCLE
// ===========================================================================
export const REQUEST_STATES = ['Draft', 'Submitted', 'Returned', 'Accepted'];
// ===========================================================================
// SCOPE CHANGE TRACKING
// ===========================================================================
/** Fields considered "scope" — changes to any of these on an Accepted project
 *  are tracked and must be reviewed by the owner or a Product admin. */
export const PROJECT_SCOPE_FIELDS = [
    'goal',
    'successMetric',
    'mustHaves',
    'outOfScope',
    'targetDate',
    'problem',
    'targetAudience',
];
// ===========================================================================
// ENUMS (stable const tuples so TypeScript can derive the union)
// ===========================================================================
export const PROJECT_CATEGORIES = [
    'Product',
    'Process',
    'Technology',
    'Data',
    'Commercial',
    'Operations',
    'Other',
];
export const PROJECT_PHASES = [
    'Intake',
    'Scoping',
    'Planning',
    'Execution',
    'Rollout',
    'Closed',
    'On Hold',
];
export const PROJECT_STATUSES = [
    'Not Started',
    'In Progress',
    'Waiting',
    'Blocked',
    'Done',
    'Cancelled',
];
export const PROJECT_HEALTH = [
    'Not Assessed',
    'On Track',
    'At Risk',
    'Off Track',
];
export const PROJECT_PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
export const DELIVERY_READINESS = [
    'Not Ready',
    'In Definition',
    'Ready for Delivery',
    'Rework Required',
];
export const DECISION_SCOPING = [
    'None',
    'Scoping Required',
    'Decision Required',
    'Alignment Required',
    'Resource Required',
    'Escalation Required',
];
export const DISCOVERY_STATUSES = [
    'Not Started',
    'In Discovery',
    'In Alignment',
    'Defined',
    'Blocked',
];
export const MILESTONE_STATUSES = ['Open', 'Done', 'At Risk'];
export const EVAL_REQUIRED = ['Yes', 'No', 'TBD'];
export const EVAL_FOCUS = [
    'Adoption / Usage',
    'Commercial Impact',
    'Process Efficiency',
    'Quality',
    'User Feedback',
    'Team / Workflow Impact',
    'Technical Performance',
    'Other',
];
export const ALREADY_FACTORED_IN = ['Yes', 'Partly', 'No', 'Not Required', 'TBD'];
export const WORKFLOW_ADJUSTMENTS = ['None', 'Minor', 'Major', 'TBD'];
export const ACTION_ITEM_STATUSES = ['Open', 'Blocked', 'Done'];
/** Built-in link types — used when `config.projectLinkTypes` is absent/empty. */
export const DEFAULT_PROJECT_LINK_TYPES = [
    'Slack Channel',
    'Meeting Notes',
    'Google Doc',
    'Drive Folder',
    'Figma',
    'Other',
];
/** Resolve the configured link types, falling back to the defaults. */
export function resolveProjectLinkTypes(config) {
    const fromConfig = config?.projectLinkTypes;
    if (Array.isArray(fromConfig) && fromConfig.length > 0) {
        return fromConfig.filter((t) => typeof t === 'string' && t.trim().length > 0);
    }
    return [...DEFAULT_PROJECT_LINK_TYPES];
}
/** Display/compare string for a links array — used by change history. */
export function stringifyProjectLinks(links) {
    if (!Array.isArray(links) || links.length === 0)
        return '';
    return links.map((l) => `${l.type}: ${l.url}`).join(' · ');
}
// ===========================================================================
// EXECUTION PLANNING — PI, sprints, phase plan
// ===========================================================================
/**
 * Program Increment naming convention: "PI: Q1/26" (quarter + 2-digit year).
 * Options are generated programmatically — no config needed.
 */
export function piLabelForDate(d) {
    const q = Math.floor(d.getUTCMonth() / 3) + 1;
    const yy = String(d.getUTCFullYear() % 100).padStart(2, '0');
    return `PI: Q${q}/${yy}`;
}
/** PI dropdown options: current quarter − 2 through + 7 (10 options). */
export function piOptions(today = new Date()) {
    const out = [];
    const base = today.getUTCFullYear() * 4 + Math.floor(today.getUTCMonth() / 3);
    for (let i = -2; i <= 7; i++) {
        const total = base + i;
        const year = Math.floor(total / 4);
        const q = (total % 4) + 1;
        out.push(`PI: Q${q}/${String(year % 100).padStart(2, '0')}`);
    }
    return out;
}
/** The phases that can carry a planned window (subset of PROJECT_PHASES). */
export const PROJECT_PLAN_PHASES = ['Scoping', 'Planning', 'Execution', 'Rollout'];
/** Display/compare string for a sprints array — used by change history. */
export function stringifyProjectSprints(sprints) {
    if (!Array.isArray(sprints) || sprints.length === 0)
        return '';
    return sprints.map((s) => `${s.name} ${s.startDate}–${s.endDate}`).join(' · ');
}
/** Display/compare string for a phase plan — used by change history. */
export function stringifyProjectPhasePlan(plan) {
    if (!plan)
        return '';
    return PROJECT_PLAN_PHASES.filter((ph) => {
        const w = plan[ph];
        return w && (w.startDate || w.endDate);
    })
        .map((ph) => {
        const w = plan[ph];
        return `${ph} ${w.startDate ?? '…'}–${w.endDate ?? '…'}`;
    })
        .join(' · ');
}
// ===========================================================================
// PARTICIPANT EMAIL HELPER
// ===========================================================================
/**
 * Compute the full participant email list for a project document.
 * Must be called with the MERGED/final values (not just the payload delta).
 */
export function computeParticipantEmails(params) {
    const set = new Set();
    if (params.requesterEmail)
        set.add(params.requesterEmail.toLowerCase());
    if (params.ownerEmail)
        set.add(params.ownerEmail.toLowerCase());
    if (params.createdByEmail)
        set.add(params.createdByEmail.toLowerCase());
    for (const cp of params.counterparts ?? []) {
        if (cp.email)
            set.add(cp.email.toLowerCase());
    }
    return Array.from(set);
}
// ===========================================================================
// LINEAR BRIDGE — read-only snapshot fetched from the Linear GraphQL API
// ===========================================================================
/** Extract the project slug from a pasted Linear URL
 *  (…linear.app/<org>/project/<slug>/…). Null when it doesn't look like one. */
export function parseLinearProjectSlug(url) {
    const m = /linear\.app\/[^/]+\/project\/([^/?#]+)/.exec(url.trim());
    return m?.[1] ?? null;
}
// ===========================================================================
// ACTIVITY FEED — /productProjects/{id}/activity (server-written timeline)
// ===========================================================================
export const PROJECT_ACTIVITY_KINDS = ['event', 'note', 'slack', 'meeting', 'decision'];
/** Health → color map (mirrors SOP conditional formatting). */
export const PROJECT_HEALTH_COLORS = {
    'On Track': 'green',
    'At Risk': 'amber',
    'Off Track': 'red',
    'Not Assessed': 'default',
};
/** Status → color map. */
export const PROJECT_STATUS_COLORS = {
    Blocked: 'red',
    'In Progress': 'default',
    'Not Started': 'default',
    Waiting: 'amber',
    Done: 'green',
    Cancelled: 'default',
};
/** Priority → color map. */
export const PROJECT_PRIORITY_COLORS = {
    Critical: 'red',
    High: 'amber',
    Medium: 'default',
    Low: 'default',
};
/** Delivery readiness → color map. */
export const DELIVERY_READINESS_COLORS = {
    'Ready for Delivery': 'green',
    'In Definition': 'amber',
    'Not Ready': 'red',
    'Rework Required': 'red',
};
/**
 * Generic flag-color resolver — takes the kind + current value and returns
 * the semantic color token. Use for table cell / badge rendering.
 *
 * Kinds:
 *   'health'            → PROJECT_HEALTH_COLORS
 *   'status'            → PROJECT_STATUS_COLORS
 *   'priority'          → PROJECT_PRIORITY_COLORS
 *   'deliveryReadiness' → DELIVERY_READINESS_COLORS
 *   'decisionScoping'   → amber when value ≠ 'None', otherwise default
 *   'milestoneStatus'   → green=Done, amber=At Risk, default=Open
 *   'actionItemStatus'  → red=Blocked, green=Done, default=Open
 */
export function projectFlagColor(kind, value) {
    switch (kind) {
        case 'health':
            return PROJECT_HEALTH_COLORS[value] ?? 'default';
        case 'status':
            return PROJECT_STATUS_COLORS[value] ?? 'default';
        case 'priority':
            return PROJECT_PRIORITY_COLORS[value] ?? 'default';
        case 'deliveryReadiness':
            return DELIVERY_READINESS_COLORS[value] ?? 'default';
        case 'decisionScoping':
            return value !== 'None' ? 'amber' : 'default';
        case 'milestoneStatus':
            if (value === 'Done')
                return 'green';
            if (value === 'At Risk')
                return 'amber';
            return 'default';
        case 'actionItemStatus':
            if (value === 'Done')
                return 'green';
            if (value === 'Blocked')
                return 'red';
            return 'default';
        default:
            return 'default';
    }
}
//# sourceMappingURL=productProjects.js.map